import React, { createContext, useEffect, useState, useMemo, ReactNode } from 'react';
import {
  PublicClientApplication,
  IPublicClientApplication,
  BrowserCacheLocation,
  Configuration,
} from '@azure/msal-browser';

interface AuthContextType {
  msalInstance: IPublicClientApplication | null;
  isInitialized: boolean;
  error: string | null;
  isDevMode?: boolean;
}

export const AuthContext = createContext<AuthContextType>({
  msalInstance: null,
  isInitialized: false,
  error: null,
  isDevMode: false,
});

type AuthProviderProps = Readonly<{
  children: ReactNode;
}>;

/**
 * AuthProvider initializes MSAL and provides it to the app
 * 
 * Required environment variables:
 * - VITE_ENTRA_TENANT_ID: Your Azure Entra ID tenant ID
 * - VITE_ENTRA_CLIENT_ID: Your app registration client ID
 * 
 * Falls back to DEV mode if env vars are not set (for local testing)
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [msalInstance, setMsalInstance] = useState<IPublicClientApplication | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDevMode, setIsDevMode] = useState(false);

  useEffect(() => {
    const initMsal = async () => {
      try {
        const tenantId = (import.meta as any).env?.VITE_ENTRA_TENANT_ID;
        const clientId = (import.meta as any).env?.VITE_ENTRA_CLIENT_ID;

        // Check if we're in dev mode (no Entra ID config)
        if (!tenantId || !clientId) {
          console.warn('[AuthProvider] Dev mode: No Entra ID credentials provided. Using local authentication.');
          setIsDevMode(true);
          setIsInitialized(true);
          return;
        }

        const config: Configuration = {
          auth: {
            clientId: clientId,
            authority: `https://login.microsoftonline.com/${tenantId}`,
            redirectUri: globalThis.location.origin,
          },
          cache: {
            cacheLocation: BrowserCacheLocation.SessionStorage,
          },
          system: {
            loggerOptions: {
              loggerCallback: (level, message, containsPii) => {
                if (containsPii) return;
                console.log(`[MSAL] ${message}`);
              },
            },
          },
        };

        const instance = new PublicClientApplication(config);
        await instance.initialize();
        setMsalInstance(instance);
        setIsInitialized(true);
      } catch (err: any) {
        const errorMessage = err?.message || 'Failed to initialize MSAL';
        console.error('[AuthProvider] Initialization error:', err);
        setError(errorMessage);
        setIsInitialized(true); // Still mark as initialized to prevent infinite loading
      }
    };

    initMsal();
  }, []);

  const contextValue = useMemo(() => ({
    msalInstance,
    isInitialized,
    error,
    isDevMode
  }), [msalInstance, isInitialized, error, isDevMode]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}
